import { isItemAuthorityCommandsFeatureEnabled } from "@/config/runtimeConfig";
import {
  createItemAuthorityOperationId,
  itemAuthorityService,
} from "@/services/itemAuthorityService";

const CANONICAL_ITEM_ID_PATTERN = /^[a-f0-9]{64}$/;
const PRIVATE_ITEM_KINDS = new Set(["product", "service"]);

export class MarketplaceItemDeletionError extends Error {
  constructor(
    code = "unknown",
    message = "This item could not be deleted.",
  ) {
    super(message);
    this.name = "MarketplaceItemDeletionError";
    this.code = code;
  }
}

function itemKind(item) {
  const kind = item?.kind || item?.type;
  return typeof kind === "string" ? kind : null;
}

function canonicalDeleteIdentity(item) {
  if (
    typeof item?.itemId !== "string" ||
    !CANONICAL_ITEM_ID_PATTERN.test(item.itemId) ||
    !Number.isSafeInteger(item.revision) ||
    item.revision < 1
  ) {
    throw new MarketplaceItemDeletionError(
      "canonical-item-required",
      "Reload your items before deleting this item.",
    );
  }
  return Object.freeze({
    itemId: item.itemId,
    expectedRevision: item.revision,
  });
}

function requireLegacyDelete(legacyDelete) {
  if (typeof legacyDelete !== "function") {
    throw new MarketplaceItemDeletionError(
      "legacy-delete-required",
      "This item could not be deleted.",
    );
  }
  return legacyDelete;
}

/**
 * Routes one Marketplace delete intent across the source-only migration seam.
 *
 * Product/service commands fail closed once private authority is enabled. Media
 * and NFT lifecycle remain outside the private item registry contract and keep
 * their existing path until their Storage/on-chain migrations are complete.
 */
export function createMarketplaceItemDeletionService({
  privateAuthorityEnabled = isItemAuthorityCommandsFeatureEnabled,
  authorityService = itemAuthorityService,
  operationIdFactory = createItemAuthorityOperationId,
} = {}) {
  const operationIds = new Map();

  const operationIdFor = ({ itemId, expectedRevision }) => {
    const key = `${itemId}:${expectedRevision}`;
    if (!operationIds.has(key)) operationIds.set(key, operationIdFactory());
    return Object.freeze({ key, operationId: operationIds.get(key) });
  };

  return Object.freeze({
    async deleteItem({ item, legacyDelete }) {
      const kind = itemKind(item);
      if (!PRIVATE_ITEM_KINDS.has(kind) || !privateAuthorityEnabled) {
        const result = await requireLegacyDelete(legacyDelete)();
        return Object.freeze({ mode: "legacy", result });
      }

      const identity = canonicalDeleteIdentity(item);
      const operation = operationIdFor(identity);
      // Delete the retry token only after a validated response. A failure keeps
      // it for lost-response replay and deliberately never invokes legacyDelete.
      const result = await authorityService.deleteOwnItem({
        operationId: operation.operationId,
        expectedRevision: identity.expectedRevision,
        itemId: identity.itemId,
      });
      operationIds.delete(operation.key);
      return Object.freeze({ mode: "private-authority", result });
    },

    clear() {
      operationIds.clear();
    },
  });
}
